/**
 * MeshNodesGlance — přehled mesh nodů (dashboard / infrastruktura / topologie).
 * Cluster a remote odděleně, všechny nody, veřejná UI adresa, třídění/filtry.
 *
 * Data: peers_list ⊕ mesh.topology (role/commit/latency/public_ui) ⊕ mesh.nodes
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Cloud,
  ExternalLink,
  GitCommit,
  Globe,
  Network,
  Radar,
  Server,
} from "lucide-react";
import { api } from "../../lib/api";
import { Badge, EmptyState, Select, StatCard, StatsGrid, StatusDot } from "../ui";
import { cn } from "../../lib/utils";
import { useMeshPeers } from "../../widgets/NodePicker";
import type { WidgetRenderProps } from "../../widgets/types";

export interface GlanceNode {
  id: string;
  name: string;
  label: string;
  host: string | null;
  online: boolean;
  isLocal: boolean;
  role: string | null;
  env: string | null;
  acmVersion: string | null;
  commit: string | null;
  arch: string | null;
  latencyMs: number | null;
  announceAgeS: number | null;
  announcing: boolean | null;
  apiPort: number | null;
  dashboardPort: number | null;
  tenantSlug: string | null;
  clusterUid: string | null;
  trustMode: string | null;
  /**
   * cluster | spoke | hub | remote | peer (mimo topologii).
   * Hub-and-spoke NENÍ cluster a spoke NENÍ remote (Filip 2026-09-10):
   * spoke je vlastní infrastruktura tohohle hubu, hub je uzel, na kterém
   * tenhle visí. Dřív obojí spadlo do „remote" a z 002 tak vypadal jeho
   * vlastní hub jako cizí mašina.
   */
  kind: "cluster" | "spoke" | "hub" | "remote" | "peer";
  /** Veřejná UI FQDN (bez https://) */
  publicUi: string | null;
  publicUiPublished: boolean;
}

interface Topology {
  ok: boolean;
  spokes?: Topology["cluster"];
  hub?: Topology["cluster"][number] | null;
  cluster: Array<{
    id: string;
    route_name: string;
    label: string;
    host: string | null;
    role: string | null;
    env: string | null;
    commit: string | null;
    acm_version: string | null;
    arch: string | null;
    announce_age_s: number | null;
    health: string;
    latency_ms: number | null;
    announcing: boolean;
    api_port: number | null;
    dashboard_port?: number | null;
    cluster_uid: string | null;
    trust_mode: string;
    public_ui?: string | null;
    public_ui_published?: boolean;
  }>;
  remote: Array<{
    id: string;
    route_name: string;
    label: string;
    host: string | null;
    role: string | null;
    env: string | null;
    commit: string | null;
    acm_version: string | null;
    arch: string | null;
    announce_age_s: number | null;
    health: string;
    latency_ms: number | null;
    announcing: boolean;
    api_port: number | null;
    dashboard_port?: number | null;
    cluster_uid: string | null;
    trust_mode: string;
    public_ui?: string | null;
    public_ui_published?: boolean;
  }>;
  discovered: Array<{ id: string; hostname: string | null; ip: string }>;
  summary: {
    cluster_total: number;
    cluster_online: number;
    remote_total: number;
    discovered_total: number;
  };
}

interface MeshNodeRow {
  node_id: string;
  stable_id?: string | null;
  hostname?: string | null;
  ip_private?: string | null;
  status?: string;
  env?: string | null;
  acm_version?: string | null;
  arch?: string | null;
  tenant_slug?: string | null;
  last_seen?: string | null;
}

type SortKey = "name" | "online" | "env" | "latency" | "public";
type FilterKey = "all" | "online" | "offline" | "public" | "no_public";

function shortCommit(c: string | null | undefined): string | null {
  if (!c || c === "unknown") return null;
  return c.length > 8 ? c.slice(0, 7) : c;
}

function fmtAge(s: number | null | undefined): string {
  if (s == null) return "—";
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
}

function fmtVersion(acm: string | null, commit: string | null): string | null {
  if (acm && acm !== "unknown") return `ACM ${acm}`;
  const sc = shortCommit(commit);
  return sc ? `git ${sc}` : null;
}

function publicUiHref(fqdn: string | null): string | null {
  if (!fqdn) return null;
  const host = fqdn.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  return host ? `https://${host}` : null;
}

/** Sjednocení peers + topology + mesh.nodes → glance nody (všechny). */
export function useMeshGlanceNodes() {
  const peersQ = useMeshPeers({ includeLocal: true });
  const topoQ = useQuery({
    queryKey: ["mesh-topology"],
    queryFn: () => api.mcp<Topology>("mesh.topology"),
    refetchInterval: 15_000,
  });
  const nodesQ = useQuery({
    queryKey: ["mesh-nodes"],
    queryFn: () =>
      api.mcp<{ ok: boolean; nodes: MeshNodeRow[] }>("mesh.nodes", {
        action: "list",
      }),
    refetchInterval: 30_000,
  });

  /** Canonical key = stable_id when known — never split one node into UUID + hostname. */
  const byKey = new Map<string, GlanceNode>();

  const findByAny = (a: string, b?: string | null): GlanceNode | undefined => {
    if (byKey.has(a)) return byKey.get(a);
    if (b && byKey.has(b)) return byKey.get(b);
    for (const n of byKey.values()) {
      if (n.id === a || n.name === a || n.label === a) return n;
      if (b && (n.id === b || n.name === b || n.label === b)) return n;
    }
    return undefined;
  };

  const ensure = (wire: string, alias?: string | null): GlanceNode => {
    const existing = findByAny(wire, alias);
    if (existing) {
      // Re-key under wire if previously stored under alias
      if (!byKey.has(wire) || byKey.get(wire) !== existing) {
        for (const [k, v] of [...byKey.entries()]) {
          if (v === existing && k !== wire) byKey.delete(k);
        }
        byKey.set(wire, existing);
      }
      return existing;
    }
    const n: GlanceNode = {
      id: wire,
      name: wire,
      label: alias && alias !== wire ? alias : wire,
      host: null,
      online: false,
      isLocal: false,
      role: null,
      env: null,
      acmVersion: null,
      commit: null,
      arch: null,
      latencyMs: null,
      announceAgeS: null,
      announcing: null,
      apiPort: null,
      dashboardPort: null,
      tenantSlug: null,
      clusterUid: null,
      trustMode: null,
      kind: "peer",
      publicUi: null,
      publicUiPublished: false,
    };
    byKey.set(wire, n);
    return n;
  };

  const peerList = Array.isArray(peersQ.data) ? peersQ.data : [];
  for (const p of peerList) {
    const wire = (p.stable_id || p.name || "").trim();
    if (!wire) continue;
    const n = ensure(wire, p.label || p.name);
    n.id = p.stable_id || wire;
    n.name = wire;
    n.label = (p.label && p.label !== wire ? p.label : n.label) || p.name || wire;
    n.host = p.host || n.host;
    n.online = p.online === true || p.status === "online";
    n.isLocal = !!p.is_local;
    n.role = n.role ?? p.role ?? null;
    n.announceAgeS = p.announce_age_s ?? n.announceAgeS;
  }

  const applyTopo = (
    rows: Topology["cluster"] | Topology["remote"] | undefined,
    kind: "cluster" | "spoke" | "hub" | "remote",
  ) => {
    for (const t of rows ?? []) {
      const routeName = String(t.route_name || t.id || "").trim();
      const stableId = String(t.id || "").trim();
      const wire = stableId || routeName;
      if (!wire) continue;
      const n = ensure(wire, t.label || routeName);
      n.id = stableId || wire;
      n.name = wire;
      n.kind = kind;
      const lab = String(t.label || "").trim();
      if (lab && lab !== wire) n.label = lab;
      else if (!n.label || n.label === n.id) n.label = routeName || wire;
      n.host = t.host || n.host;
      n.role = t.role ?? n.role;
      n.env = t.env ?? n.env;
      n.commit = t.commit ?? n.commit;
      n.acmVersion = t.acm_version && t.acm_version !== "unknown"
        ? t.acm_version
        : n.acmVersion;
      n.arch = t.arch ?? n.arch;
      n.latencyMs = t.latency_ms ?? n.latencyMs;
      n.announceAgeS = t.announce_age_s ?? n.announceAgeS;
      n.announcing = t.announcing;
      n.apiPort = t.api_port ?? n.apiPort;
      n.dashboardPort = t.dashboard_port ?? n.dashboardPort;
      n.clusterUid = t.cluster_uid ?? n.clusterUid;
      n.trustMode = t.trust_mode ?? n.trustMode;
      if (t.public_ui) {
        n.publicUi = t.public_ui;
        n.publicUiPublished = t.public_ui_published === true;
      }
      if (t.health === "online" || t.health === "degraded") n.online = true;
      else if (t.health === "offline" && n.kind === kind) {
        if (n.announceAgeS == null || n.announceAgeS > 180) n.online = false;
      }
    }
  };
  applyTopo(topoQ.data?.cluster, "cluster");
  applyTopo(topoQ.data?.spokes, "spoke");
  applyTopo(topoQ.data?.hub ? [topoQ.data.hub] : undefined, "hub");
  applyTopo(topoQ.data?.remote, "remote");

  for (const m of nodesQ.data?.nodes ?? []) {
    const wire = String(m.stable_id || m.node_id || "").trim();
    if (!wire) continue;
    const node = ensure(wire, m.hostname || m.node_id);
    node.id = m.stable_id || wire;
    node.name = wire;
    if (m.hostname && (node.label === node.id || node.label === m.node_id)) {
      node.label = m.hostname;
    }
    node.host = m.ip_private || node.host;
    node.env = m.env ?? node.env;
    if (m.acm_version && m.acm_version !== "unknown") {
      node.acmVersion = m.acm_version;
    }
    node.arch = m.arch ?? node.arch;
    node.tenantSlug = m.tenant_slug ?? node.tenantSlug;
    if (node.kind === "peer") node.kind = "cluster";
  }

  // Final collapse: one card per stable_id / id
  const collapsed = new Map<string, GlanceNode>();
  for (const n of byKey.values()) {
    const k = n.id || n.name;
    const prev = collapsed.get(k);
    if (!prev) {
      collapsed.set(k, n);
      continue;
    }
    // Prefer richer label, keep online if either is
    if (n.label && n.label !== k && (prev.label === k || !prev.label)) {
      prev.label = n.label;
    }
    if (n.online) prev.online = true;
    if (n.host && !prev.host) prev.host = n.host;
    if (n.kind !== "peer") prev.kind = n.kind;
    if (n.isLocal) prev.isLocal = true;
    if (n.publicUi && !prev.publicUi) {
      prev.publicUi = n.publicUi;
      prev.publicUiPublished = n.publicUiPublished;
    } else if (n.publicUiPublished && !prev.publicUiPublished) {
      prev.publicUi = n.publicUi ?? prev.publicUi;
      prev.publicUiPublished = true;
    }
  }
  const nodes = [...collapsed.values()];

  return {
    nodes,
    summary: topoQ.data?.summary ?? null,
    discovered: topoQ.data?.discovered ?? [],
    isLoading: peersQ.isLoading && topoQ.isLoading,
    isError: peersQ.isError && topoQ.isError,
  };
}

function sortNodes(list: GlanceNode[], sort: SortKey): GlanceNode[] {
  const arr = [...list];
  arr.sort((a, b) => {
    // local vždy první v rámci sekce
    if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1;
    switch (sort) {
      case "online":
        if (a.online !== b.online) return a.online ? -1 : 1;
        break;
      case "env": {
        const c = (a.env || "zzz").localeCompare(b.env || "zzz", "cs");
        if (c !== 0) return c;
        break;
      }
      case "latency": {
        const la = a.latencyMs ?? 999999;
        const lb = b.latencyMs ?? 999999;
        if (la !== lb) return la - lb;
        break;
      }
      case "public": {
        const pa = a.publicUi ? 0 : 1;
        const pb = b.publicUi ? 0 : 1;
        if (pa !== pb) return pa - pb;
        break;
      }
      default:
        break;
    }
    return a.label.localeCompare(b.label, "cs");
  });
  return arr;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Uzel zaregistrovaný jen routing identitou (capability routing, hub relay)
 * nemá lidské jméno — label je pak rovný jeho UUID. Vypsat ho celý vypadá
 * jako rozbitá karta, tak se zkrátí; plná hodnota zůstává v title.
 */
function displayLabel(n: GlanceNode): string {
  const label = (n.label ?? "").trim();
  if (label && !UUID_RE.test(label)) return label;
  const id = label || n.name || n.id || "";
  return id ? `${id.slice(0, 8)}…` : "—";
}

function filterNodes(list: GlanceNode[], filter: FilterKey): GlanceNode[] {
  switch (filter) {
    case "online":
      return list.filter((n) => n.online);
    case "offline":
      return list.filter((n) => !n.online);
    case "public":
      return list.filter((n) => !!n.publicUi);
    case "no_public":
      return list.filter((n) => !n.publicUi);
    default:
      return list;
  }
}

function NodeGlanceRow({
  n,
  dense,
  onOpen,
}: {
  n: GlanceNode;
  dense?: boolean;
  onOpen: (id: string, isLocal: boolean, name: string) => void;
}) {
  const { t } = useTranslation();
  const ver = fmtVersion(n.acmVersion, n.commit);
  const pubHref = publicUiHref(n.publicUi);

  return (
    <div
      className={cn(
        "w-full rounded-xl border transition-colors",
        "border-gray-800/80 bg-gray-900/30 hover:border-gray-600 hover:bg-gray-800/40",
        dense ? "px-2.5 py-2" : "px-3 py-2.5",
        !n.online && "opacity-75",
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(n.id, n.isLocal, n.name)}
        className="w-full text-left"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <StatusDot status={n.online ? "running" : "stopped"} />
          <span
            className="text-sm font-semibold text-white truncate"
            title={n.label}
          >
            {displayLabel(n)}
          </span>
          {n.isLocal && (
            <Badge variant="blue">{t("mesh.localBadge", "lokál")}</Badge>
          )}
          {n.role && (
            <Badge variant={n.role === "master" ? "purple" : "blue"}>
              {n.role}
            </Badge>
          )}
          {n.env && (
            <Badge
              variant={n.env === "production"
                ? "red"
                : n.env === "staging"
                ? "yellow"
                : "gray"}
            >
              {n.env}
            </Badge>
          )}
          {n.kind === "remote" && (
            <Badge variant="yellow">{t("mesh.remoteBadge", "remote")}</Badge>
          )}
          {n.announcing === false && n.online && (
            <Badge variant="yellow">
              {t("mesh.noAnnounce", "bez announce")}
            </Badge>
          )}
          <span className="ml-auto text-[10px] text-gray-500 tabular-nums shrink-0">
            {fmtAge(n.announceAgeS)}
          </span>
        </div>

        <div
          className={cn(
            "mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono",
            dense ? "text-gray-500" : "text-gray-400",
          )}
        >
          <span className="text-gray-300" title="IP / host">
            {n.host || "—"}
            {n.apiPort ? `:${n.apiPort}` : ""}
          </span>
          {ver && (
            <span className="inline-flex items-center gap-1 text-emerald-400/90">
              <GitCommit size={10} />
              {ver}
            </span>
          )}
          {n.arch && <span className="text-gray-600">{n.arch}</span>}
          {n.latencyMs != null && (
            <span className="text-sky-400/80">{n.latencyMs} ms</span>
          )}
          {n.tenantSlug && (
            <span className="text-cyan-400/80">{n.tenantSlug}</span>
          )}
          {n.trustMode === "upstream" && (
            <span className="text-amber-400/80">trust → hub</span>
          )}
          <span className={n.online ? "text-emerald-500" : "text-red-400"}>
            {n.online ? "online" : "offline"}
          </span>
        </div>
      </button>

      {/* Veřejná UI adresa — mimo navigate button (externí odkaz) */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
        {pubHref
          ? (
            <a
              href={pubHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "inline-flex items-center gap-1 font-mono truncate max-w-full",
                n.publicUiPublished
                  ? "text-blue-400 hover:text-blue-300"
                  : "text-amber-400/90 hover:text-amber-300",
              )}
              title={n.publicUiPublished
                ? t("mesh.publicUiOpen", "Otevřít veřejné UI")
                : t("mesh.publicUiPending", "Veřejná UI (pending publish)")}
            >
              <Globe size={11} className="shrink-0" />
              <span className="truncate">{n.publicUi}</span>
              {!n.publicUiPublished && (
                <Badge variant="warning" className="text-[9px]">
                  pending
                </Badge>
              )}
              <ExternalLink size={10} className="shrink-0 opacity-70" />
            </a>
          )
          : (
            <span className="inline-flex items-center gap-1 text-gray-600">
              <Globe size={11} />
              {t("mesh.noPublicUi", "bez veřejné UI")}
            </span>
          )}
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  icon: Icon,
  accent,
  nodes,
  dense,
  onOpen,
  empty,
}: {
  title: string;
  hint?: string;
  icon: typeof Server;
  accent?: "default" | "amber";
  nodes: GlanceNode[];
  dense?: boolean;
  onOpen: (id: string, isLocal: boolean, name: string) => void;
  empty: string;
}) {
  if (nodes.length === 0) {
    return (
      <div
        className={cn(
          "rounded-xl border overflow-hidden",
          accent === "amber"
            ? "border-amber-500/25"
            : "border-gray-800/80",
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-2 border-b",
            accent === "amber"
              ? "border-amber-500/20 bg-amber-500/5"
              : "border-gray-800 bg-gray-900/40",
          )}
        >
          <Icon
            size={14}
            className={accent === "amber" ? "text-amber-400" : "text-emerald-400"}
          />
          <h3
            className={cn(
              "text-xs font-semibold uppercase tracking-wider",
              accent === "amber" ? "text-amber-300" : "text-gray-300",
            )}
          >
            {title}
          </h3>
          <span className="text-[10px] text-gray-600">0</span>
          {hint && (
            <span className="text-[10px] text-gray-600 truncate hidden sm:inline">
              {hint}
            </span>
          )}
        </div>
        <div className="px-3 py-4 text-xs text-gray-600 text-center">{empty}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden",
        accent === "amber" ? "border-amber-500/25" : "border-gray-800/80",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 border-b",
          accent === "amber"
            ? "border-amber-500/20 bg-amber-500/5"
            : "border-gray-800 bg-gray-900/40",
        )}
      >
        <Icon
          size={14}
          className={accent === "amber" ? "text-amber-400" : "text-emerald-400"}
        />
        <h3
          className={cn(
            "text-xs font-semibold uppercase tracking-wider",
            accent === "amber" ? "text-amber-300" : "text-gray-300",
          )}
        >
          {title}
        </h3>
        <span
          className={cn(
            "text-[10px] tabular-nums",
            accent === "amber" ? "text-amber-600/80" : "text-gray-500",
          )}
        >
          {nodes.filter((n) => n.online).length}/{nodes.length}
        </span>
        {hint && (
          <span
            className={cn(
              "text-[10px] truncate hidden sm:inline",
              accent === "amber" ? "text-amber-600/70" : "text-gray-600",
            )}
          >
            {hint}
          </span>
        )}
      </div>
      <div className="p-2 space-y-2">
        {nodes.map((n) => (
          <NodeGlanceRow
            key={`${n.kind}-${n.name}-${n.id}`}
            n={n}
            dense={dense}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

export interface MeshNodesGlanceProps {
  format?: "sm" | "md" | "lg";
  viewport?: "mobile" | "desktop";
  showSummary?: boolean;
  /** Max počet řádků; undefined = vše (infra lg). */
  limit?: number;
  className?: string;
}

export function MeshNodesGlance({
  format = "md",
  viewport = "desktop",
  showSummary,
  limit,
  className,
}: MeshNodesGlanceProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nodes, summary, discovered, isLoading, isError } =
    useMeshGlanceNodes();

  const [sort, setSort] = useState<SortKey>("name");
  const [filter, setFilter] = useState<FilterKey>("all");

  const withSummary = showSummary ?? format === "lg";
  const dense = format === "sm";
  // lg / infrastruktura: VŠECHNY nody; sm/md mají strop pro dashboard glance
  const cap = limit ??
    (format === "lg"
      ? undefined
      : viewport === "mobile"
      ? 8
      : format === "sm"
      ? 6
      : 16);

  const filtered = useMemo(() => {
    let list = filterNodes(nodes, filter);
    list = sortNodes(list, sort);
    return list;
  }, [nodes, filter, sort]);

  // Tenhle uzel a to, co pod něj patří: vlastní cluster (kde existuje),
  // hub, na kterém visíme, a spoky, které visí na nás.
  const cluster = filtered.filter((n) =>
    n.kind === "cluster" || n.kind === "spoke" || n.kind === "hub" || n.isLocal
  );
  // Lokální uzel patří vždy do clusteru, i když ho topologie vede jako remote
  // (spoke pod cizím hubem). Bez téhle podmínky se self vykreslil dvakrát —
  // jednou jako člen clusteru, podruhé jako „cizí hub-linked instance".
  const remote = filtered.filter((n) => n.kind === "remote" && !n.isLocal);
  const peersOnly = filtered.filter(
    (n) => n.kind === "peer" && !n.isLocal,
  );
  const hasRemoteInInventory = nodes.some((n) => n.kind === "remote");

  // Pro flat (sm) view se limituje celkový seznam
  const flatShown = cap != null ? filtered.slice(0, cap) : filtered;
  const useSections = format === "lg" || format === "md";

  const onlineN = nodes.filter((n) => n.online).length;
  const publicN = nodes.filter((n) => !!n.publicUi).length;

  const openNode = (id: string, isLocal: boolean, name: string) => {
    // Workspace URL = stable_id (UUID) — tool routing i Phase 14 identity.
    // Label (MPPACM01) je jen v kartě; target_node nesmí být jen hostname.
    const key = isLocal ? "local" : encodeURIComponent(id || name);
    navigate(`/infra/nodes/${key}`);
  };

  if (isLoading && nodes.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        {t("mesh.loadingNodes", "Načítám nody…")}
      </div>
    );
  }
  if (isError && nodes.length === 0) {
    return (
      <div className="text-sm text-red-400 py-8 text-center">
        {t("mesh.nodesLoadError", "Nepodařilo se načíst mesh nody")}
      </div>
    );
  }

  return (
    <div className={cn("h-full flex flex-col gap-3 min-h-0", className)}>
      <div className="flex items-center justify-between gap-2 px-0.5 shrink-0 flex-wrap">
        <span className="text-xs text-gray-500">
          {t("dashboard.meshNodesTitle", "Mesh nody")}{" "}
          <span className="text-gray-400">
            ({onlineN}/{nodes.length} online
            {publicN > 0
              ? ` · ${t("mesh.publicUiCountN", "{{count}} veřejných UI", { count: publicN })}`
              : ""})
          </span>
        </span>
        <Link
          to="/core/mesh"
          className="text-xs text-blue-400 hover:text-blue-300 shrink-0"
        >
          → {t("dashboard.detailLink", "Infrastruktura")}
        </Link>
      </div>

      {withSummary && (
        <StatsGrid columns={4}>
          <StatCard
            icon={Server}
            label={t("mesh.clusterOnline", "Cluster online")}
            value={summary
              ? `${summary.cluster_online}/${summary.cluster_total}`
              : `${cluster.filter((n) => n.online).length}/${cluster.length}`}
            variant={
              (summary
                  ? summary.cluster_online === summary.cluster_total
                  : cluster.every((n) => n.online))
                ? "success"
                : "warning"
            }
          />
          <StatCard
            icon={Cloud}
            label={t("mesh.remoteInstances", "Remote")}
            value={summary?.remote_total ?? remote.length}
          />
          <StatCard
            icon={Globe}
            label={t("mesh.publicUiCount", "Veřejné UI")}
            value={publicN}
          />
          <StatCard
            icon={Network}
            label={t("mesh.nodesTotal", "Nody")}
            value={nodes.length}
          />
        </StatsGrid>
      )}

      {/* Třídění + filtr */}
      {(format === "lg" || format === "md") && nodes.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="text-xs"
          >
            <option value="name">{t("mesh.sortName", "Název")}</option>
            <option value="online">{t("mesh.sortOnline", "Online first")}</option>
            <option value="env">{t("mesh.sortEnv", "Prostředí")}</option>
            <option value="latency">{t("mesh.sortLatency", "Latence")}</option>
            <option value="public">
              {t("mesh.sortPublic", "Veřejná UI first")}
            </option>
          </Select>
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterKey)}
            className="text-xs"
          >
            <option value="all">{t("mesh.filterAll", "Vše")}</option>
            <option value="online">{t("mesh.filterOnline", "Online")}</option>
            <option value="offline">{t("mesh.filterOffline", "Offline")}</option>
            <option value="public">
              {t("mesh.filterPublic", "S veřejnou UI")}
            </option>
            <option value="no_public">
              {t("mesh.filterNoPublic", "Bez veřejné UI")}
            </option>
          </Select>
        </div>
      )}

      {filtered.length === 0
        ? (
          <EmptyState
            title={t("dashboard.noMeshNodes", "Žádné mesh nody")}
            description={t(
              "mesh.noNodesHint",
              "Přidej peer v Infrastruktuře nebo počkej na announce.",
            )}
          />
        )
        : useSections
        ? (
          <div className="space-y-3 flex-1 min-h-0 overflow-auto">
            <Section
              title={t("mesh.sectionCluster", "MESH cluster")}
              hint={t("mesh.sectionClusterHint", "lokální členové")}
              icon={Server}
              nodes={cluster}
              dense={dense}
              onOpen={openNode}
              empty={t("mesh.sectionClusterEmpty", "Žádné cluster nody")}
            />
            {(remote.length > 0 || hasRemoteInInventory) && (
              <Section
                title={t("mesh.sectionRemote", "Remote instance")}
                hint={t(
                  "mesh.sectionRemoteHint",
                  "cizí hub-linked — ne součást clusteru",
                )}
                icon={Cloud}
                accent="amber"
                nodes={remote}
                dense={dense}
                onOpen={openNode}
                empty={t("mesh.sectionRemoteEmpty", "Žádné remote nody (filtr)")}
              />
            )}
            {peersOnly.length > 0 && (
              <Section
                title={t("mesh.sectionPeers", "Další peery")}
                hint={t("mesh.sectionPeersHint", "mimo topologii")}
                icon={Network}
                nodes={peersOnly}
                dense={dense}
                onOpen={openNode}
                empty=""
              />
            )}
          </div>
        )
        : (
          <div className="space-y-2 flex-1 min-h-0 overflow-auto">
            {flatShown.map((n) => (
              <NodeGlanceRow
                key={`${n.kind}-${n.name}-${n.id}`}
                n={n}
                dense={dense}
                onOpen={openNode}
              />
            ))}
            {filtered.length > flatShown.length && (
              <Link
                to="/core/mesh"
                className="block text-center text-xs text-gray-500 hover:text-blue-400 py-1"
              >
                +{filtered.length - flatShown.length}{" "}
                {t("dashboard.moreNodes", "dalších → Infrastruktura")}
              </Link>
            )}
          </div>
        )}

      {withSummary && discovered.length > 0 && (
        <div className="rounded-xl border border-gray-800/60 p-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-gray-600 px-1">
            {t("mesh.discovered", "Discovered")}
          </div>
          {discovered.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-400"
            >
              <Radar size={12} className="text-sky-500 shrink-0" />
              <span className="truncate">{d.hostname || d.id}</span>
              <span className="ml-auto font-mono text-gray-600">{d.ip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** WidgetShell entry — dashboard / panel / registry. */
export function MeshNodesGlanceWidget({ format, viewport }: WidgetRenderProps) {
  return (
    <MeshNodesGlance
      format={format}
      viewport={viewport}
      showSummary={format === "lg"}
    />
  );
}

export default MeshNodesGlance;
